import React from 'react';
import styled from 'astroturf/react';

import type { StatLayout } from './arms';
import { NotificationDot } from '../buttonStyles';
import { formatCount } from './formatCount';

export const STACKED_TILE_WIDTH = 68;
export const INLINE_TILE_WIDTH = 140;

const TileRoot = styled.a<{ layout: StatLayout }>`
  @import 'stylesheets/design_system/font';
  @include sans-serif;

  position: relative;
  display: flex;
  flex: 0 0 auto;
  box-sizing: border-box;
  color: var(--text-primary, #070a15);
  text-decoration: none;

  &:hover,
  &:visited {
    color: var(--text-primary, #070a15);
    text-decoration: none;
  }

  &.layout-stacked {
    width: ${STACKED_TILE_WIDTH}px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  &.layout-inline {
    width: ${INLINE_TILE_WIDTH}px;
    flex-direction: row;
    align-items: center;
    column-gap: 4px;
  }
`;

const CountRow = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  column-gap: 4px;
  min-width: 0;
`;

const Count = styled.span`
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  white-space: nowrap;
`;

const AdornmentSlot = styled.span`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
`;

const Label = styled.span`
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  color: var(--text-secondary, #535366);
  font-size: 11px;
  font-weight: 500;
  line-height: 14px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

type ImpactStatTileProps = {
  count: number | null;
  label: string;
  layout: StatLayout;
  adornment?: React.ReactNode;
  href?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  showDot?: boolean;
};

const ImpactStatTile = ({
  count,
  label,
  layout,
  adornment,
  href,
  onClick,
  showDot = true,
}: ImpactStatTileProps) => {
  if (count == null) {
    return null;
  }

  return (
    <TileRoot
      data-testid="impact-stat-tile"
      layout={layout}
      href={href}
      onClick={onClick}
    >
      <CountRow>
        <Count data-testid="impact-stat-count">{formatCount(count)}</Count>
        {adornment != null && (
          <AdornmentSlot data-testid="impact-stat-adornment">
            {adornment}
          </AdornmentSlot>
        )}
      </CountRow>
      <Label>{label}</Label>
      {showDot && <NotificationDot data-testid="impact-stat-dot" />}
    </TileRoot>
  );
};

export default ImpactStatTile;
